import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { PasswordField, PhoneField, TextField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { AcceptTermsCheckbox } from '@/features/identity/components/AcceptTermsCheckbox';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { PasswordRequirements } from '@/features/identity/components/PasswordRequirements';
import { PasswordStrengthIndicator } from '@/features/identity/components/PasswordStrengthIndicator';
import { useRegisterForm } from '@/features/identity/hooks/useRegisterForm';

/** The Create Account screen's form. */
export function RegisterForm() {
  const { form, onSubmit, serverError } = useRegisterForm();
  const { control, formState } = form;
  const password = form.watch('password');
  const termsAccepted = form.watch('termsAccepted');

  return (
    <AuthCard
      footer={
        <>
          Already have an account? <RouterLink to={ROUTES.login}>Sign in</RouterLink>
        </>
      }
    >
      <AuthHeader title="Create your account" subtitle="Join Willnq to get started." />
      <Stack component="form" onSubmit={onSubmit} spacing={2} noValidate>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        <Stack direction="row" spacing={2}>
          <TextField
            name="firstName"
            control={control}
            label="First name"
            autoComplete="given-name"
          />
          <TextField
            name="lastName"
            control={control}
            label="Last name"
            autoComplete="family-name"
          />
        </Stack>
        <TextField name="email" control={control} label="Email" type="email" autoComplete="email" />
        <PhoneField name="phone" control={control} label="Phone (optional)" />
        <PasswordField
          name="password"
          control={control}
          label="Password"
          autoComplete="new-password"
        />
        <PasswordStrengthIndicator password={password} />
        <PasswordRequirements password={password} />
        <PasswordField
          name="confirmPassword"
          control={control}
          label="Confirm password"
          autoComplete="new-password"
        />
        <AcceptTermsCheckbox name="termsAccepted" control={control} />
        <Button
          type="submit"
          variant="contained"
          size="large"
          // Disabled until the box is checked, so the requirement reads as a precondition rather than
          // as a submission that gets rejected. The schema still enforces it, and so does the API —
          // this is the affordance, never the control.
          disabled={formState.isSubmitting || !termsAccepted}
          startIcon={
            formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Create account
        </Button>
      </Stack>
    </AuthCard>
  );
}
